import { apiClient } from "api/trpc/trpc-client"
import OrgGuardStore from "features/main/main-router/org-guard/OrgGuardStore"
import WebSocketEventClientListUpdate from "../websockets/models/WebSocketEventClientListUpdate"
import { AdvisorClientDto } from '@gap/backend/app-services/advisor/client/models/AdvisorClientDto'
import Loader from "reusable/mobx-helpers/Loader"
import { makeAutoObservable } from "mobx"
import { activeClientStatuses, clientStatuses } from '@gap/utils/clients/clientStatus'

interface ClientsCacheStoreProps {
   orgGuardStore: OrgGuardStore
}

export default class ClientsCacheStore {
   sp
   clientsLoader = new Loader(false)
   private _clients: AdvisorClientDto.idWithName[]

   constructor(sp: ClientsCacheStoreProps) {
      makeAutoObservable(this)
      this.sp = sp
   }

   /** Every client the user can see, whatever its status. */
   get clients(): AdvisorClientDto.idWithName[] {
      if (!this._clients) {
         this._clients = []
      }
      return this._clients
   }

   /** The clients a user may pick for new work. */
   get selectableClients(): AdvisorClientDto.idWithName[] {
      return this.clients.filter(client => activeClientStatuses.some(status => status === client.status))
   }

   /**
    * Looks up a client by advisor-client id, including archived ones.
    * @param advisorClientId id of the advisor client to find
    * @returns the matching client, or undefined when none is cached
    */
   findClient = (advisorClientId: string | null | undefined): AdvisorClientDto.idWithName | undefined => {
      if (!advisorClientId) return undefined
      return this.clients.find(client => client.id === advisorClientId)
   }

   /**
    * Adds a client to the cache unless one with the same id is already cached.
    * @param client the client to add
    */
   addClient = (client: AdvisorClientDto.idWithName) => {
      if (this.findClient(client.id)) return
      this.clients.push(client)
   }

   handleClientsEvent = (clientsUpdateEvent: WebSocketEventClientListUpdate) => {
      switch (clientsUpdateEvent.action) {
         case 'add':
         case 'update':
            return this.onClientUpdate(clientsUpdateEvent)
         case 'sync':
            return this.fetch()
      }
   }

   onClientUpdate = (clientUpdateEvent: WebSocketEventClientListUpdate) => {
      switch (clientUpdateEvent.action) {
         case 'add':
            this._clients.push(clientUpdateEvent.client)
            break
         case 'update':
            this._clients = this._clients.map(client => {
               if (client.id === clientUpdateEvent.client.id) {
                  return clientUpdateEvent.client
               }
               return client
            })
            break
      }
   }

   fetch = async () => {
      await this.clientsLoader.executeLoading(async () => {
         if (this.sp.orgGuardStore.userOrg.orgType === 'advisor' && !this.sp.orgGuardStore.userOrg.isDeactivated) {
            this._clients = await apiClient.advisor.getMinimalClients.query({ status: [...clientStatuses] })
         }
      })
   }
}
